import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { ClinicCollectionMethod } from "@customTypes/clinic";
import { Pagination } from "@customTypes/index";

import { clinicQKeys } from "./queryKeys";


interface QueryProps {
  pagination?: Partial<Pagination>;
  search?: string;
  isEnabled?: boolean;
}

const queryFn = ({ pagination, search }: QueryProps) => {
  return api.get("clinic/collection-methods", {
    params: {
      ...(pagination && {
        page: pagination.page,
        page_size: pagination.pageSize,
      }),
      ...(search && { search }),
    },
  });
};

const select = (res: any) => {
  // Check if response has pagination structure
  if (res.data.data) {
    const pagination: Pagination = {
      page: res.data.pagination.page,
      pageCount: res.data.pagination.page_count,
      pageSize: res.data.pagination.page_size,
      total: res.data.pagination.total,
    };

    const newData: ClinicCollectionMethod[] = [];

    for (const i in res.data.data) {
      const { collection_method, _id, collection_price, created_at, updated_at } = res.data.data[i];

      newData.push({
        id: _id,
        method: collection_method || "",
        price: collection_price || 0,
        createdAt: created_at ? new Date(created_at) : new Date(),
        updatedAt: updated_at ? new Date(updated_at) : new Date(),
      });
    }

    return {
      newData,
      pagination,
    };
  }

  // Handle direct array response (non-paginated)
  if (Array.isArray(res.data) && res.data.length) {
    const newData: ClinicCollectionMethod[] = [];

    for (const item of res.data) {
      const { collection_method, _id, collection_price, created_at, updated_at } = item;

      newData.push({
        id: _id,
        method: collection_method || "",
        price: collection_price || 0,
        createdAt: created_at ? new Date(created_at) : new Date(),
        updatedAt: updated_at ? new Date(updated_at) : new Date(),
      });
    }

    return {
      newData,
      pagination: {
        page: 1,
        pageCount: 1,
        pageSize: 25, // Use consistent default pageSize instead of newData.length
        total: newData.length,
      },
    };
  }

  return {
    newData: [],
    pagination: {
      page: 1,
      pageCount: 1,
      pageSize: 25, // Use consistent default pageSize instead of 0
      total: 0,
    },
  };
};

export const useGetClinicCollectionMethods = ({
  pagination,
  search,
  isEnabled = true,
}: QueryProps = {}) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: [
      ...clinicQKeys.collectionMethodsDetailed(),
      { pagination, search },
    ],
    queryFn: () => queryFn({ pagination, search }),
    select,
    staleTime: Infinity,
    enabled: isEnabled,

    onError: (res: any) => {
      console.error("[From Get All Clinic Collection Methods]", res);
      handleErrors(res, "query");
    },
  });
};
